/**
 * Shared avatar source/fallback helpers used by both internal teammate
 * avatars and external talent / artist contact avatars.
 *
 * Resolution order matches the sidebar / "My Profile" behavior so freshly
 * cropped company photos appear consistently anywhere an avatar is
 * rendered. For talent/artist contacts (which only carry `photoUrl`) the
 * helper falls through to that field so a single Avatar pattern works
 * across the app:
 *
 *   1. The personal profile's `profilePicture` (the company-managed,
 *      square-cropped photo uploaded via My Profile).
 *   2. The auth provider's `profileImageUrl` (e.g. Replit / Google avatar).
 *   3. The talent/artist contact's `photoUrl`.
 *   4. `undefined` so the AvatarFallback (initials) takes over.
 */

export interface AvatarUserShape {
  profilePicture?: string | null;
  profileImageUrl?: string | null;
  photoUrl?: string | null;
}

/**
 * Returns the best available image URL for an avatar, or `undefined` so
 * the consumer's AvatarFallback (initials) renders.
 *
 * Accepts `null` / `undefined` for the user so callers can pass loading
 * states without extra guards.
 */
export function getAvatarSource(
  user: AvatarUserShape | null | undefined,
): string | undefined {
  if (!user) return undefined;
  return (
    user.profilePicture || user.profileImageUrl || user.photoUrl || undefined
  );
}

/**
 * Builds 1- or 2-letter initials from a display name. Falls back to "U"
 * when no usable name is provided so AvatarFallback always has content.
 */
export function getAvatarInitials(name: string | null | undefined): string {
  const trimmed = (name || "").trim();
  if (!trimmed) return "U";
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}
